import { useEffect, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import TrendChart from "@/components/TrendChart";
import PdfButton from "@/components/PdfButton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Scales, ArrowRight, CheckCircle, House, MapPin } from "@phosphor-icons/react";
import MortgageCalculator from "@/components/MortgageCalculator";
import CityCombobox from "@/components/CityCombobox";
import InfoTip from "@/components/InfoTip";
import ServiceIcon from "@/components/ServiceIcon";

function ZonePicker({ side, cities, city, setCity, zone, setZone, zones }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className={`w-6 h-6 flex items-center justify-center font-mono-data text-xs font-bold ${side === "A" ? "bg-lime-400 text-lime-950" : "bg-cyan-500 text-cyan-950"}`}>{side}</span>
        <span className="font-mono-data text-xs text-zinc-400 tracking-widest">SLOT {side}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="font-mono-data text-[10px] text-zinc-500 tracking-widest">CITTÀ</Label>
          <div className="mt-1">
            <CityCombobox value={city} onChange={(v) => { setCity(v); setZone(""); }} cities={cities} testId={`compare-city-${side}`} />
          </div>
        </div>
        <div>
          <Label className="font-mono-data text-[10px] text-zinc-500 tracking-widest">QUARTIERE</Label>
          <Select value={zone || "_none"} onValueChange={(v) => setZone(v === "_none" ? "" : v)}>
            <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10" data-testid={`compare-zone-${side}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent className="rounded-none bg-zinc-900 border-white/10">
              <SelectItem value="_none">—</SelectItem>
              {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function PropertyPicker({ side, cities, city, setCity, zone, setZone, zones, listings, listingId, setListingId }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className={`w-6 h-6 flex items-center justify-center font-mono-data text-xs font-bold ${side === "A" ? "bg-lime-400 text-lime-950" : "bg-cyan-500 text-cyan-950"}`}>{side}</span>
        <span className="font-mono-data text-xs text-zinc-400 tracking-widest">SLOT {side}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="font-mono-data text-[10px] text-zinc-500 tracking-widest">CITTÀ</Label>
          <div className="mt-1">
            <CityCombobox value={city} onChange={(v) => { setCity(v); setZone(""); setListingId(""); }} cities={cities} testId={`p-city-${side}`} />
          </div>
        </div>
        <div>
          <Label className="font-mono-data text-[10px] text-zinc-500 tracking-widest">QUARTIERE</Label>
          <Select value={zone || "_all"} onValueChange={(v) => { setZone(v === "_all" ? "" : v); setListingId(""); }}>
            <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10" data-testid={`p-zone-${side}`}><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent className="rounded-none bg-zinc-900 border-white/10">
              <SelectItem value="_all">Tutti</SelectItem>
              {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="font-mono-data text-[10px] text-zinc-500 tracking-widest">IMMOBILE</Label>
        <Select value={listingId || "_none"} onValueChange={(v) => setListingId(v === "_none" ? "" : v)}>
          <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10" data-testid={`p-listing-${side}`}><SelectValue placeholder="Seleziona immobile" /></SelectTrigger>
          <SelectContent className="rounded-none bg-zinc-900 border-white/10 max-h-72">
            <SelectItem value="_none">—</SelectItem>
            {listings.map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.type === "vendita" ? "🏠" : "🔑"} {l.zone} · {l.sqm}m² · {formatEuro(l.price)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-[10px] font-mono-data text-zinc-600 mt-1">{listings.length} immobili disponibili</div>
      </div>
    </div>
  );
}

function StatRow({ label, a, b, format = (x) => x, better = "high", suffix = "", info = null }) {
  const va = a == null ? null : Number(a);
  const vb = b == null ? null : Number(b);
  let winner = null;
  if (better !== "neutral" && va != null && vb != null && va !== vb) {
    winner = better === "high" ? (va > vb ? "a" : "b") : (va < vb ? "a" : "b");
  }
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2 border-b border-white/5 font-mono-data text-sm">
      <div className={`text-right ${winner === "a" ? "text-lime-400 font-bold" : "text-zinc-300"}`}>{va == null ? "—" : `${format(va)}${suffix}`}</div>
      <div className="text-[10px] text-zinc-500 tracking-widest text-center whitespace-nowrap flex items-center justify-center gap-1.5">
        <span>{label}</span>
        {info && <InfoTip title={label} testId={`tip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{info}</InfoTip>}
      </div>
      <div className={`text-left ${winner === "b" ? "text-cyan-400 font-bold" : "text-zinc-300"}`}>{vb == null ? "—" : `${format(vb)}${suffix}`}</div>
    </div>
  );
}

function StatRowText({ label, a, b }) {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2 border-b border-white/5 font-mono-data text-sm">
      <div className="text-right text-zinc-300">{a || "—"}</div>
      <div className="text-[10px] text-zinc-500 tracking-widest text-center whitespace-nowrap">{label}</div>
      <div className="text-left text-zinc-300">{b || "—"}</div>
    </div>
  );
}

function BoolRow({ label, a, b }) {
  const cell = (v) => v == null ? "—" : (v ? "✓" : "✗");
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2 border-b border-white/5 font-mono-data text-sm">
      <div className={`text-right ${a ? "text-lime-400" : "text-zinc-500"}`}>{cell(a)}</div>
      <div className="text-[10px] text-zinc-500 tracking-widest text-center whitespace-nowrap">{label}</div>
      <div className={`text-left ${b ? "text-cyan-400" : "text-zinc-500"}`}>{cell(b)}</div>
    </div>
  );
}

export default function Confronto() {
  const [mode, setMode] = useState("zones"); // 'zones' | 'properties'
  const [cities, setCities] = useState([]);

  // zone mode state
  const [zonesA, setZonesA] = useState([]);
  const [zonesB, setZonesB] = useState([]);
  const [cityA, setCityA] = useState("milano");
  const [cityB, setCityB] = useState("milano");
  const [zoneA, setZoneA] = useState("");
  const [zoneB, setZoneB] = useState("");
  const [statsA, setStatsA] = useState(null);
  const [statsB, setStatsB] = useState(null);

  // property mode state
  const [pCityA, setPCityA] = useState("milano");
  const [pCityB, setPCityB] = useState("milano");
  const [pZoneA, setPZoneA] = useState("");
  const [pZoneB, setPZoneB] = useState("");
  const [pZonesA, setPZonesA] = useState([]);
  const [pZonesB, setPZonesB] = useState([]);
  const [listingsA, setListingsA] = useState([]);
  const [listingsB, setListingsB] = useState([]);
  const [lidA, setLidA] = useState("");
  const [lidB, setLidB] = useState("");
  const [analysisA, setAnalysisA] = useState(null);
  const [analysisB, setAnalysisB] = useState(null);

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);
  // zones fetch (both modes)
  useEffect(() => { api.get(`/cities/${cityA}/zones`).then((r) => setZonesA(r.data)); }, [cityA]);
  useEffect(() => { api.get(`/cities/${cityB}/zones`).then((r) => setZonesB(r.data)); }, [cityB]);
  useEffect(() => { api.get(`/cities/${pCityA}/zones`).then((r) => setPZonesA(r.data)); }, [pCityA]);
  useEffect(() => { api.get(`/cities/${pCityB}/zones`).then((r) => setPZonesB(r.data)); }, [pCityB]);

  useEffect(() => {
    if (cityA && zoneA) api.get(`/zones/${cityA}/${encodeURIComponent(zoneA)}/stats`).then((r) => setStatsA(r.data)).catch(() => setStatsA(null));
    else setStatsA(null);
  }, [cityA, zoneA]);
  useEffect(() => {
    if (cityB && zoneB) api.get(`/zones/${cityB}/${encodeURIComponent(zoneB)}/stats`).then((r) => setStatsB(r.data)).catch(() => setStatsB(null));
    else setStatsB(null);
  }, [cityB, zoneB]);

  // fetch listings for property pickers
  useEffect(() => {
    if (!pCityA) return;
    const params = new URLSearchParams({ city: pCityA, type: "vendita", limit: "60" });
    if (pZoneA) params.append("zone", pZoneA);
    api.get(`/listings?${params.toString()}`).then((r) => setListingsA(r.data));
  }, [pCityA, pZoneA]);
  useEffect(() => {
    if (!pCityB) return;
    const params = new URLSearchParams({ city: pCityB, type: "vendita", limit: "60" });
    if (pZoneB) params.append("zone", pZoneB);
    api.get(`/listings?${params.toString()}`).then((r) => setListingsB(r.data));
  }, [pCityB, pZoneB]);

  // fetch full analysis when a listing is picked
  useEffect(() => {
    if (lidA) api.get(`/property/${lidA}/analysis`).then((r) => setAnalysisA(r.data)).catch(() => setAnalysisA(null));
    else setAnalysisA(null);
  }, [lidA]);
  useEffect(() => {
    if (lidB) api.get(`/property/${lidB}/analysis`).then((r) => setAnalysisB(r.data)).catch(() => setAnalysisB(null));
    else setAnalysisB(null);
  }, [lidB]);

  const cityName = (id) => (cities.find((c) => c.id === id) || {}).name || id;

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto flex items-end gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <ServiceIcon name="valuation" size={64} withGlow />
            <div>
              <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-2">— CONFRONTO · PROPERTY VALUATION</div>
              <h1 className="font-display font-black text-3xl">Analisi side-by-side.</h1>
              <p className="text-sm text-zinc-400 mt-1 hidden md:block">Valuta due immobili o due zone fianco a fianco con metriche finanziarie complete.</p>
            </div>
          </div>
          <div className="ml-auto" data-tour-id="tour-compare-mode">
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="rounded-none bg-zinc-900 border border-white/10 p-0 h-11">
                <TabsTrigger value="zones" className="rounded-none data-[state=active]:bg-lime-400 data-[state=active]:text-lime-950 px-5 font-mono-data text-xs" data-testid="compare-mode-zones">
                  <MapPin size={12} weight="bold" className="mr-1.5" /> ZONE
                </TabsTrigger>
                <TabsTrigger value="properties" className="rounded-none data-[state=active]:bg-lime-400 data-[state=active]:text-lime-950 px-5 font-mono-data text-xs" data-testid="compare-mode-properties">
                  <House size={12} weight="bold" className="mr-1.5" /> IMMOBILI
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {mode === "zones" ? (
          <>
            {/* Zone pickers */}
            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 items-center border border-white/10 p-5">
              <ZonePicker side="A" cities={cities} city={cityA} setCity={setCityA} zone={zoneA} setZone={setZoneA} zones={zonesA} />
              <div className="flex items-center justify-center">
                <ArrowRight size={22} weight="bold" className="text-zinc-600 rotate-90 md:rotate-0" />
              </div>
              <ZonePicker side="B" cities={cities} city={cityB} setCity={setCityB} zone={zoneB} setZone={setZoneB} zones={zonesB} />
            </div>

            {/* Zone stats */}
            <div className="border border-white/10 p-5">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center pb-3 border-b border-white/10 mb-3">
                <div className="text-right">
                  <div className="font-mono-data text-[10px] text-lime-400 tracking-widest">SLOT A</div>
                  <div className="font-display font-black text-xl">{zoneA ? `${zoneA}, ${cityName(cityA)}` : "—"}</div>
                  {zoneA && (
                    <div className="mt-2 flex justify-end">
                      <PdfButton kind="zone" id={`${cityA}/${encodeURIComponent(zoneA)}`} label="PDF zona" />
                    </div>
                  )}
                </div>
                <div className="text-center text-[10px] font-mono-data text-zinc-500 tracking-widest px-4">CONFRONTA</div>
                <div className="text-left">
                  <div className="font-mono-data text-[10px] text-cyan-400 tracking-widest">SLOT B</div>
                  <div className="font-display font-black text-xl">{zoneB ? `${zoneB}, ${cityName(cityB)}` : "—"}</div>
                  {zoneB && (
                    <div className="mt-2">
                      <PdfButton kind="zone" id={`${cityB}/${encodeURIComponent(zoneB)}`} label="PDF zona" />
                    </div>
                  )}
                </div>
              </div>
              {(statsA || statsB) ? (
                <div data-testid="compare-stats-table">
                  <StatRow label="€/m² VENDITA" a={statsA?.avg_price_sqm} b={statsB?.avg_price_sqm} format={(v) => formatEuro(Math.round(v))} better="low" info="Prezzo medio al m² delle vendite in zona. È il benchmark per capire se un singolo immobile è sopra o sotto mercato." />
                  <StatRow label="€/m² AFFITTO" a={statsA?.avg_rent_sqm} b={statsB?.avg_rent_sqm} format={(v) => `€${v}`} better="high" info="Canone medio mensile al m². Moltiplica per la superficie per stimare il canone realistico di un immobile in quel quartiere." />
                  <StatRow label="RENDIMENTO" a={statsA?.yield_pct} b={statsB?.yield_pct} format={(v) => v.toFixed(2)} suffix="%" better="high" info="Rendimento lordo della zona (affitto annuo / prezzo × 100). Confronta con la tua soglia minima nella Dashboard." />
                  <StatRow label="M² MEDI" a={statsA?.avg_sqm} b={statsB?.avg_sqm} format={(v) => v.toFixed(0)} suffix=" m²" better="high" />
                  <StatRow label="INSERZIONI VENDITA" a={statsA?.n_sale} b={statsB?.n_sale} better="neutral" />
                  <StatRow label="INSERZIONI AFFITTO" a={statsA?.n_rent} b={statsB?.n_rent} better="neutral" />
                  <StatRow label="% ASCENSORE" a={statsA?.amenities_pct?.elevator} b={statsB?.amenities_pct?.elevator} suffix="%" better="high" />
                  <StatRow label="% TERRAZZO" a={statsA?.amenities_pct?.terrace} b={statsB?.amenities_pct?.terrace} suffix="%" better="high" />
                  <StatRow label="% GIARDINO" a={statsA?.amenities_pct?.garden} b={statsB?.amenities_pct?.garden} suffix="%" better="high" />
                  <StatRow label="% BOX AUTO" a={statsA?.amenities_pct?.garage} b={statsB?.amenities_pct?.garage} suffix="%" better="high" />
                </div>
              ) : (
                <div className="text-center font-mono-data text-xs text-zinc-500 py-8">Seleziona due quartieri per iniziare il confronto.</div>
              )}
            </div>

            {(zoneA || zoneB) && (
              <div className="grid md:grid-cols-2 gap-6">
                {zoneA && (
                  <div className="border border-white/10 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="rounded-none bg-lime-400 text-lime-950 font-mono-data text-[10px] hover:bg-lime-400">A</Badge>
                      <span className="font-mono-data text-xs text-zinc-400">TREND {zoneA.toUpperCase()} · {cityName(cityA).toUpperCase()}</span>
                    </div>
                    <TrendChart city={cityA} zone={zoneA} months={24} height={220} />
                  </div>
                )}
                {zoneB && (
                  <div className="border border-white/10 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="rounded-none bg-cyan-500 text-cyan-950 font-mono-data text-[10px] hover:bg-cyan-500">B</Badge>
                      <span className="font-mono-data text-xs text-zinc-400">TREND {zoneB.toUpperCase()} · {cityName(cityB).toUpperCase()}</span>
                    </div>
                    <TrendChart city={cityB} zone={zoneB} months={24} height={220} />
                  </div>
                )}
              </div>
            )}

            {statsA && statsB && (
              <div className="border border-lime-400/30 bg-lime-400/[0.03] p-5 flex gap-4 items-start">
                <CheckCircle size={22} weight="bold" className="text-lime-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-bold text-lg mb-1">Verdetto</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {statsA.yield_pct > statsB.yield_pct
                      ? <><span className="text-lime-400 font-bold">{zoneA}</span> offre un rendimento superiore ({statsA.yield_pct}% vs {statsB.yield_pct}%). </>
                      : statsB.yield_pct > statsA.yield_pct
                        ? <><span className="text-cyan-400 font-bold">{zoneB}</span> offre un rendimento superiore ({statsB.yield_pct}% vs {statsA.yield_pct}%). </>
                        : "I due quartieri hanno rendimenti equivalenti. "}
                    {statsA.avg_price_sqm && statsB.avg_price_sqm && (
                      <>Ma il ticket d'ingresso in <span className={statsA.avg_price_sqm < statsB.avg_price_sqm ? "text-lime-400" : "text-cyan-400"}>
                        {statsA.avg_price_sqm < statsB.avg_price_sqm ? zoneA : zoneB}
                      </span> è più basso ({formatEuro(Math.round(Math.min(statsA.avg_price_sqm, statsB.avg_price_sqm)))}/m²).</>
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Property pickers */}
            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 items-start border border-white/10 p-5">
              <PropertyPicker side="A" cities={cities} city={pCityA} setCity={setPCityA} zone={pZoneA} setZone={setPZoneA} zones={pZonesA} listings={listingsA} listingId={lidA} setListingId={setLidA} />
              <div className="flex items-center justify-center pt-8">
                <ArrowRight size={22} weight="bold" className="text-zinc-600 rotate-90 md:rotate-0" />
              </div>
              <PropertyPicker side="B" cities={cities} city={pCityB} setCity={setPCityB} zone={pZoneB} setZone={setPZoneB} zones={pZonesB} listings={listingsB} listingId={lidB} setListingId={setLidB} />
            </div>

            {/* Property compare */}
            <PropertyCompareTable A={analysisA} B={analysisB} cityName={cityName} />
          </>
        )}
      </div>
    </div>
  );
}

function PropertyCompareTable({ A, B, cityName }) {
  const lA = A?.listing;
  const lB = B?.listing;
  const fA = A?.financials;
  const fB = B?.financials;

  return (
    <div className="space-y-6" data-testid="property-compare-block">
      {/* Header row with titles + PDF */}
      <div className="border border-white/10 p-5">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center pb-3 border-b border-white/10 mb-3">
          <div className="text-right">
            <div className="font-mono-data text-[10px] text-lime-400 tracking-widest">SLOT A</div>
            <div className="font-display font-black text-xl">{lA ? lA.title : "—"}</div>
            {lA && (
              <div className="mt-1 text-[11px] font-mono-data text-zinc-500">
                {lA.address} · {lA.zone}, {cityName(lA.city)}
                <span className="ml-2 px-1.5 py-0.5 bg-zinc-800 text-zinc-400 text-[10px]">{lA.source}</span>
              </div>
            )}
            {lA && (
              <div className="mt-2 flex justify-end">
                <PdfButton kind="property" id={lA.id} label="PDF report" />
              </div>
            )}
          </div>
          <div className="text-center text-[10px] font-mono-data text-zinc-500 tracking-widest px-4">CONFRONTA</div>
          <div className="text-left">
            <div className="font-mono-data text-[10px] text-cyan-400 tracking-widest">SLOT B</div>
            <div className="font-display font-black text-xl">{lB ? lB.title : "—"}</div>
            {lB && (
              <div className="mt-1 text-[11px] font-mono-data text-zinc-500">
                {lB.address} · {lB.zone}, {cityName(lB.city)}
                <span className="ml-2 px-1.5 py-0.5 bg-zinc-800 text-zinc-400 text-[10px]">{lB.source}</span>
              </div>
            )}
            {lB && (
              <div className="mt-2">
                <PdfButton kind="property" id={lB.id} label="PDF report" />
              </div>
            )}
          </div>
        </div>

        {(lA || lB) ? (
          <>
            {/* Financial block */}
            <div className="font-mono-data text-[10px] text-lime-400 tracking-widest mt-4 mb-1">FINANZIARIO</div>
            <StatRow label="PREZZO" a={lA?.price} b={lB?.price} format={(v) => formatEuro(v)} better="low" info="Prezzo di richiesta. Ricorda che il prezzo di chiusura è tipicamente il 5-10% più basso in Italia. Non è il prezzo giusto ma il ticket d'ingresso." />
            <StatRow label="€/M²" a={lA?.price_per_sqm} b={lB?.price_per_sqm} format={(v) => formatEuro(Math.round(v))} better="low" info="Il prezzo per metro quadro è il KPI di confronto principale. Verificalo sempre contro la media della zona (visibile nella modalità Zone)." />
            <StatRow label="CANONE STIMATO/MESE" a={fA?.est_monthly_rent} b={fB?.est_monthly_rent} format={(v) => formatEuro(v)} better="high" info="Stima basata sul canone medio della zona per m². Confermala consultando annunci di affitto simili prima di firmare." />
            <StatRow label="RENDIMENTO LORDO" a={fA?.gross_yield_pct} b={fB?.gross_yield_pct} format={(v) => v.toFixed(2)} suffix="%" better="high" info="Formula: (canone × 12) / prezzo × 100. Sotto 4% = zona premium. 4-6% = equilibrio sano. Sopra 7% = alta resa ma verifica il rischio (periferie, sfitto, morosità)." />
            <StatRow label="CAP RATE" a={fA?.cap_rate_pct} b={fB?.cap_rate_pct} format={(v) => v.toFixed(2)} suffix="%" better="high" info="Rendimento NETTO dopo IMU, condominio, manutenzione e sfitto (~25% del canone). È la metrica che le banche guardano davvero. Sopra il 4% è considerato solido." />
            <StatRow label="RATA MENSILE MUTUO" a={fA?.monthly_mortgage} b={fB?.monthly_mortgage} format={(v) => formatEuro(v)} better="low" info="Rata calcolata con LTV 80%, tasso 3,5% fisso, 25 anni. Il canone deve coprire almeno la rata + le spese fisse per avere cash flow positivo." />
            <StatRow label="CAPITALE PROPRIO" a={fA?.down_payment} b={fB?.down_payment} format={(v) => formatEuro(v)} better="low" info="Anticipo del 20% richiesto dalla banca. Aggiungi ~10-12% per spese: notaio, imposte di registro, agenzia, ristrutturazione." />
            <StatRow label="CASH FLOW ANNUO NETTO" a={fA?.net_annual_cashflow} b={fB?.net_annual_cashflow} format={(v) => formatEuro(v)} better="high" info="Canone annuo − rata mutuo − spese. Se è positivo l'immobile si autofinanzia; se negativo devi metterci soldi ogni mese." />
            <StatRow label="CASH-ON-CASH RETURN" a={fA?.coc_return_pct} b={fB?.coc_return_pct} format={(v) => v.toFixed(2)} suffix="%" better="high" info="Il KPI più importante per investitori con leva. Cash flow annuo / capitale proprio investito. Sopra il 6% è buono, sopra il 10% è eccezionale." />
            <StatRow label="PAYBACK (ANNI)" a={fA?.payback_years} b={fB?.payback_years} format={(v) => v.toFixed(1)} better="low" info="Anni per recuperare il capitale proprio investito con il cash flow. Sotto 15 = eccellente. Sopra 30 = valuta LTV inferiore o rinegoziazione." />

            {/* Characteristics */}
            <div className="font-mono-data text-[10px] text-lime-400 tracking-widest mt-6 mb-1">CARATTERISTICHE</div>
            <StatRow label="SUPERFICIE" a={lA?.sqm} b={lB?.sqm} suffix=" m²" better="high" />
            <StatRow label="LOCALI" a={lA?.rooms} b={lB?.rooms} better="neutral" />
            <StatRow label="BAGNI" a={lA?.bathrooms} b={lB?.bathrooms} better="neutral" />
            <StatRow label="ANNO COSTRUZIONE" a={lA?.year_built} b={lB?.year_built} better="high" />
            <StatRowText label="PIANO" a={lA?.floor} b={lB?.floor} />
            <StatRowText label="CLASSE ENERGETICA" a={lA?.energy_class} b={lB?.energy_class} />
            <StatRowText label="CONDIZIONI" a={lA?.condition} b={lB?.condition} />
            <StatRowText label="RISCALDAMENTO" a={lA?.heating} b={lB?.heating} />
            <StatRowText label="ESPOSIZIONE" a={lA?.exposure} b={lB?.exposure} />

            {/* Amenities */}
            <div className="font-mono-data text-[10px] text-lime-400 tracking-widest mt-6 mb-1">DOTAZIONI</div>
            <BoolRow label="ASCENSORE" a={lA?.elevator} b={lB?.elevator} />
            <BoolRow label="TERRAZZO" a={lA?.terrace} b={lB?.terrace} />
            <BoolRow label="GIARDINO" a={lA?.garden} b={lB?.garden} />
            <BoolRow label="BOX AUTO" a={lA?.garage} b={lB?.garage} />
            <BoolRow label="ARREDATO" a={lA?.furnished} b={lB?.furnished} />
          </>
        ) : (
          <div className="text-center font-mono-data text-xs text-zinc-500 py-12">Seleziona un immobile in ciascuno slot per il confronto side-by-side.</div>
        )}
      </div>

      {(lA || lB) && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="property-trend-charts">
          {lA && (
            <div className="border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="rounded-none bg-lime-400 text-lime-950 font-mono-data text-[10px] hover:bg-lime-400">A</Badge>
                <span className="font-mono-data text-xs text-zinc-400">STORICO €/M² · {lA.zone.toUpperCase()} · {cityName(lA.city).toUpperCase()}</span>
              </div>
              <TrendChart city={lA.city} zone={lA.zone} months={24} height={180} />
              <div className="mt-2 text-[10px] font-mono-data text-zinc-500">
                Dati aggregati del quartiere. L'immobile è quotato a <span className="text-lime-400">€ {Math.round(lA.price_per_sqm)}/m²</span>.
              </div>
            </div>
          )}
          {lB && (
            <div className="border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="rounded-none bg-cyan-500 text-cyan-950 font-mono-data text-[10px] hover:bg-cyan-500">B</Badge>
                <span className="font-mono-data text-xs text-zinc-400">STORICO €/M² · {lB.zone.toUpperCase()} · {cityName(lB.city).toUpperCase()}</span>
              </div>
              <TrendChart city={lB.city} zone={lB.zone} months={24} height={180} />
              <div className="mt-2 text-[10px] font-mono-data text-zinc-500">
                Dati aggregati del quartiere. L'immobile è quotato a <span className="text-cyan-400">€ {Math.round(lB.price_per_sqm)}/m²</span>.
              </div>
            </div>
          )}
        </div>
      )}

      {A && B && fA && fB && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="mortgage-calcs">
          <div>
            <div className="font-mono-data text-[10px] text-lime-400 tracking-widest mb-2">CALCOLATORE MUTUO · SLOT A</div>
            <MortgageCalculator
              initialPrice={lA.price}
              initialDownPct={20}
              initialRate={3.5}
              initialYears={25}
              monthlyRent={fA.est_monthly_rent || 0}
              monthlyExpenses={Math.round((fA.annual_expenses || 0) / 12)}
            />
          </div>
          <div>
            <div className="font-mono-data text-[10px] text-cyan-400 tracking-widest mb-2">CALCOLATORE MUTUO · SLOT B</div>
            <MortgageCalculator
              initialPrice={lB.price}
              initialDownPct={20}
              initialRate={3.5}
              initialYears={25}
              monthlyRent={fB.est_monthly_rent || 0}
              monthlyExpenses={Math.round((fB.annual_expenses || 0) / 12)}
            />
          </div>
        </div>
      )}

      {A && B && fA && fB && (
        <div className="border border-lime-400/30 bg-lime-400/[0.03] p-5 flex gap-4 items-start" data-testid="property-verdict">
          <CheckCircle size={22} weight="bold" className="text-lime-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-lg mb-1">Verdetto investimento</div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {fA.coc_return_pct != null && fB.coc_return_pct != null ? (
                fA.coc_return_pct > fB.coc_return_pct
                  ? <><span className="text-lime-400 font-bold">Slot A</span> ha un cash-on-cash superiore ({fA.coc_return_pct}% vs {fB.coc_return_pct}%). </>
                  : fB.coc_return_pct > fA.coc_return_pct
                    ? <><span className="text-cyan-400 font-bold">Slot B</span> ha un cash-on-cash superiore ({fB.coc_return_pct}% vs {fA.coc_return_pct}%). </>
                    : "I due immobili hanno cash-on-cash equivalente. "
              ) : null}
              {lA && lB && (
                <>Il ticket più leggero è <span className={lA.price < lB.price ? "text-lime-400" : "text-cyan-400"}>Slot {lA.price < lB.price ? "A" : "B"}</span> ({formatEuro(Math.min(lA.price, lB.price))}). </>
              )}
              {fA.payback_years && fB.payback_years && Math.min(fA.payback_years, fB.payback_years) <= 60 ? (
                <>Il payback più rapido è <span className={fA.payback_years < fB.payback_years ? "text-lime-400" : "text-cyan-400"}>Slot {fA.payback_years < fB.payback_years ? "A" : "B"}</span> ({Math.min(fA.payback_years, fB.payback_years).toFixed(1)} anni).</>
              ) : (
                <>Con le assunzioni correnti (LTV 80%, tasso 3,5%, 25 anni) il payback stimato supera i 60 anni: valutare un LTV inferiore o rinegoziare le condizioni.</>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
